import { useCallback, useRef } from 'react';
import { termMatchesOrders } from './utils';

/**
 * Decides what the manifest grid's search box starts with. The carried term is handed over once,
 * the first time the page shows the grid, and only when it matches an order or item the grid has
 * loaded; otherwise the grid starts empty. After `clearSeed`, every later mount starts empty.
 * @param {object} options
 * @param {string} options.carried The term the page was opened with ('' when none).
 * @param {Array<object>} options.orders Orders the grid will show.
 * @param {boolean} options.loaded Whether the manifest data has loaded.
 * @returns {{ ready: boolean, seedTerm: string, clearSeed: () => void }} `ready` is false while a
 * carried term waits for data; `seedTerm` is the initial grid term; call `clearSeed` when the grid
 * unmounts (for example on a tab switch).
 */
export const useGridSeedTerm = ({ carried, orders, loaded }) => {
    const seedRef = useRef(null);
    const ready = !carried || loaded || seedRef.current !== null;
    if (seedRef.current === null && ready) {
        seedRef.current = carried && termMatchesOrders(orders, carried) ? carried : '';
    }
    const clearSeed = useCallback(() => {
        seedRef.current = '';
    }, []);
    return { ready, seedTerm: seedRef.current ?? '', clearSeed };
};
